// frontend/src/services/accountService.js
import axiosInstance from './axiosConfig';

/**
 * Fetches the account profile data for the currently logged-in user.
 * This includes Account details (like accountName, primaryContactEmail, etc.) 
 * and the user's login username.
 * @returns {Promise<object>} A promise resolving to the account profile data. 
 * Expected format: { ...accountFields, primaryContactEmail: '...', username: '...' }
 */
export const getAccountProfile = async () => {
    try {
        const response = await axiosInstance.get('/account/profile');
        // The backend now sends the full account object (which includes primaryContactEmail)
        // and also attaches the user's username to the response.
        return response.data;
    } catch (error) {
        console.error("API Error fetching account profile:", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to fetch account profile');
    }
};

/**
 * Updates the account profile data for the currently logged-in user.
 * @param {object} profileData - The profile data to update.
 * Expected fields: accountName, primaryContactFullName, primaryContactPhoneNumber,
 * primaryContactAddress, responsiblePersons (array of objects), primaryContactEmail.
 * @returns {Promise<object>} A promise resolving to the backend response (likely the updated profile).
 */
export const updateAccountProfile = async (profileData) => {
    try {
        // profileData will now include primaryContactEmail if sent from the component
        const response = await axiosInstance.put('/account/profile', profileData);
        return response.data;
    } catch (error) {
        console.error("API Error updating account profile:", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to update account profile');
    }
};

/**
 * Fetches a list of all accounts (for admin use).
 * @returns {Promise<Array>} A promise resolving to an array of account objects (e.g., [{ id, accountName }]).
 */
export const listAllAccounts = async () => {
    try {
        // This corresponds to GET /account/ in your accountRoutes.js
        const response = await axiosInstance.get('/account');
        return response.data || [];
    } catch (error) {
        console.error("API Error listing all accounts:", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to list accounts');
    }
};

/**
 * Creates a new account (for admin use).
 * @param {object} accountData - Data for the new account, 
 * typically { accountName, primaryContactFullName?, primaryContactEmail?, ... }.
 * @returns {Promise<object>} A promise resolving to the newly created account object.
 */
export const createAccountByAdmin = async (accountData) => {
    try {
        // This corresponds to POST /account/ in your accountRoutes.js
        const response = await axiosInstance.post('/account', accountData);
        return response.data;
    } catch (error) {
        console.error("API Error creating account by admin:", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to create account');
    }
};

/**
 * Updates an account (for admin use).
 * @param {object} accountData - Data for the account to update, must include id.
 * @returns {Promise<object>} A promise resolving to the updated account object.
 */
export const updateAccountByAdmin = async (accountData) => {
    try {
        const { id, ...data } = accountData;
        const response = await axiosInstance.put(`/account/${id}`, data);
        return response.data;
    } catch (error) {
        console.error(`API Error updating account ${accountData.id} by admin:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to update account');
    }
};